import { cva, cx } from '@vizit/theme';
import { useMemo } from 'react';
import { ProgressBar, type ProgressBarProps, composeRenderProps } from 'react-aria-components';

interface RadialProgressProps extends ProgressBarProps {
  /**
   * Classes to apply to the bar element
   *
   * @optional
   * @type string
   */
  barClassName?: string;
  /**
   * Classes to apply to the fill element
   *
   * @optional
   * @type string
   */
  fillClassName?: string;
  /**
   * The maximum value of the progress bar
   *
   * @optional
   * @type number
   * @default 100
   */
  maxValue?: number;
  /**
   * The size of the progress bar
   * Sizes score-* are used for the score radial progress component
   * and reflect untitledUI naming for that component
   *
   * @optional
   * @type 8 | 12 | 16 | 20 | 24 | 32 | 40 | 80
   * @default 12
   */
  size?: 8 | 12 | 16 | 20 | 24 | 32 | 40 | 80;
  /**
   * The width of the progress bar
   *
   * @optional
   * @type number
   * @default 4
   */
  strokeWidth?: number;
  /**
   * The current value of the progress bar
   *
   * @optional
   * @type number
   * @default 0
   */
  value?: number;
}

const styles = {
  root: cva({
    base: 'relative align-middle w-fit',
  }),
  svg: cva({
    base: 'h-32 w-32',
    variants: {
      isIndeterminate: {
        true: 'animate-spin',
      },
      size: {
        8: 'h-8 w-8',
        12: 'h-12 w-12',
        16: 'h-16 w-16',
        20: 'h-20 w-20',
        24: 'h-24 w-24',
        32: 'h-32 w-32', // used for score progress xxs
        40: 'h-40 w-40', // used for score progress xxs-alt
        80: 'h-80 w-80', // used for score progress xs
      },
    },
    defaultVariants: {
      size: 24,
    },
  }),
};

/**
 * The RadialProgress component is used to display a loading state.
 *
 * @param props - The props for the RadialProgress component.
 * @example
 * <RadialProgress />
 */
function RadialProgress(props: RadialProgressProps) {
  const {
    className: classNameProp,
    barClassName,
    children,
    fillClassName,
    maxValue = 100,
    size = 24,
    strokeWidth = 4,
    value = 0,
    ...other
  } = props;

  const sanitizedValue = useMemo(() => {
    if (value < 0) {
      return 0;
    }

    if (value > maxValue) {
      return maxValue;
    }

    return value;
  }, [maxValue, value]);

  const center = 16;
  const radius = center - strokeWidth;
  const circumference = 2 * radius * Math.PI;

  return (
    <ProgressBar
      className={composeRenderProps(classNameProp, (className, renderProps) =>
        cx('vzt-radial-progress', styles.root({ ...renderProps, className })),
      )}
      formatOptions={{ style: 'decimal' }}
      maxValue={maxValue}
      minValue={0}
      value={sanitizedValue}
      {...other}
    >
      {({ defaultChildren, isIndeterminate, percentage = 0, valueText }) => (
        <>
          <svg
            className={cx('vzt-radial-progress__svg', styles.svg({ isIndeterminate, size }))}
            fill="none"
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              className={cx('vzt-radial-progress__svg__bar stroke-gray-light-300', barClassName)}
              cx={center}
              cy={center}
              r={radius}
              strokeWidth={strokeWidth}
            />
            <circle
              className={cx('vzt-radial-progress__svg__bar-value stroke-brand-600', fillClassName)}
              cx={center}
              cy={center}
              r={radius}
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={circumference - (isIndeterminate ? 0.34 : percentage / 100) * circumference}
              strokeWidth={strokeWidth}
              transform={`rotate(-90 ${center} ${center})`}
            />
          </svg>
          {typeof children === 'function'
            ? children({ defaultChildren, isIndeterminate, percentage, valueText })
            : children}
        </>
      )}
    </ProgressBar>
  );
}

export { RadialProgress, type RadialProgressProps };
